import { Checkbox, Text, Textarea, useToastContext } from "@ttc3k/trekker";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { WhereaboutsModal } from "shared/components";
import { Box, Flex } from "styled-system/jsx";
import type { SetOrderArrival } from "./arrivalUtils";

type MarkArrivedModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  selectedAttendanceKeys: string[];
  currentSessionId: string;
  /** All sessions this order/pass is valid for; shows the "all days" option when > 1. */
  allSessionIds: string[];
  onSetArrival: SetOrderArrival;
};

export const MarkArrivedModal = ({
  isOpen,
  onClose,
  orderId,
  selectedAttendanceKeys,
  currentSessionId,
  allSessionIds,
  onSetArrival,
}: MarkArrivedModalProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "CALENDAR.SLIDEOUT.ARRIVAL",
  });
  const { toastFactory } = useToastContext();
  const [note, setNote] = useState("");
  const [allDays, setAllDays] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isMultiDay = allSessionIds.length > 1;

  useEffect(() => {
    if (isOpen) {
      setNote("");
      setAllDays(false);
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSetArrival({
        orderId,
        sessionIds: allDays && isMultiDay ? allSessionIds : [currentSessionId],
        attendanceKeys: selectedAttendanceKeys,
        arrived: true,
        note: note.trim() || undefined,
      });
      onClose();
    } catch {
      toastFactory.create({
        title: t("ERROR"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("MODAL_TITLE")}
      description={t("MODAL_DESCRIPTION", {
        count: selectedAttendanceKeys.length,
      })}
      size="sm"
      isMaxZIndex
      saveButtonCopy={t("CONFIRM")}
      saveButtonProps={{
        onClick: handleSave,
        isLoading: isSaving,
      }}
    >
      <Flex flexDir="column" gap="300">
        <Box>
          <Text visual="smallSemiBold" color="text.dark" mb="100">
            {t("NOTE_LABEL")}
          </Text>
          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={t("NOTE_PLACEHOLDER")}
            rows={3}
          />
        </Box>

        {isMultiDay && (
          <Flex alignItems="flex-start" gap="150">
            <Checkbox
              checked={allDays}
              onCheckedChange={({ checked }) => setAllDays(!!checked)}
            />
            <Box>
              <Text visual="smallSemiBold" color="text.dark">
                {t("ALL_DAYS_LABEL")}
              </Text>
              <Text visual="smallRegular" color="text.mid">
                {t("ALL_DAYS_HINT", { count: allSessionIds.length })}
              </Text>
            </Box>
          </Flex>
        )}
      </Flex>
    </WhereaboutsModal>
  );
};
